import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fmtDateTime, relative } from '../../lib/when';
import { Dialog } from './TodoPeople';
import { useConfirm } from '../ConfirmDialog';
import { Icon } from './icons';

/*
 * 097: "Subscribe in your calendar" - a person's private iCalendar address.
 *
 * Gordon, 28 October: the Outlook sync is parked, so each person gets the
 * thing every calendar already understands - an address to SUBSCRIBE to that
 * carries their dated to-dos and their Jarvis meetings. This is the button
 * on the Calendar view and the small dialog behind it.
 *
 * THE ADDRESS IS SHOWN ONCE. The server keeps only a hash of the token (the
 * contractor sessions and guest links are kept the same way), so the address
 * is on screen when it is made or regenerated and never again: the dialog
 * says so, and "Regenerate" is the way to a fresh one - which stops the old
 * one working, so it is asked about first.
 *
 *   subscribe   { get, create, update, remove } - promises against
 *               /todos/calendar/link (TodoApp.js builds them with the same
 *               token and the same `as=` as everything else)
 *   narrow      a phone: the dialog is a sheet (TodoPeople's Dialog does that)
 */
const INSTRUCTIONS = [
  ['Outlook', 'Add calendar › Subscribe from web, paste the address.'],
  ['Apple Calendar', 'File › New Calendar Subscription, paste the address.'],
  ['Google Calendar', 'Other calendars › + › From URL, paste the address.']
];

const lower = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

const SWITCHES = [
  ['include_todos', 'My dated to-dos', 'Every to-do with a date, on every list you are on - your Private list too. Timed ones at their time; done ones only with the switch below.'],
  ['include_meetings', 'My Jarvis meetings', 'The meetings you organise or are invited to and have not declined.'],
  ['include_done', 'Completed to-dos too', 'Done to-dos from the last 60 days, marked “Done”.']
];

export default function CalendarSubscribe({ subscribe, narrow = false }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);        // { ready, enabled, link }
  const [fresh, setFresh] = useState(null);        // the address, while it is on screen
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const addressRef = useRef(null);

  const load = useCallback(() => subscribe.get()
    .then((r) => { setState(r); setProblem(null); })
    .catch((err) => setProblem(err?.response?.data?.error || 'Could not read your calendar link')), [subscribe]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (!copied) return undefined; const t = setTimeout(() => setCopied(false), 1800); return () => clearTimeout(t); }, [copied]);

  const run = async (fn, fail) => {
    setBusy(true); setProblem(null);
    try { await fn(); } catch (err) { setProblem(err?.response?.data?.error || fail); } finally { setBusy(false); }
  };

  const make = () => run(async () => {
    const r = await subscribe.create();
    setState({ ready: true, enabled: true, link: r.link });
    setFresh(r.link?.url || null);
  }, 'Could not make the link');

  const regenerate = async () => {
    const ok = await confirm({
      title: 'Make a new address?',
      message: 'The address you have now stops working at once. Any calendar subscribed to it has to be given the new one.',
      confirmLabel: 'Make a new address'
    });
    if (ok) await make();
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Remove your calendar link?',
      message: 'The address stops working at once and the calendars subscribed to it stop updating. You can make a new one at any time.',
      confirmLabel: 'Remove', danger: true
    });
    if (!ok) return;
    await run(async () => { await subscribe.remove(); setFresh(null); setState((s) => ({ ...(s || {}), link: null })); }, 'Could not remove the link');
  };

  const toggle = (key, value) => run(async () => {
    const r = await subscribe.update({ [key]: value });
    setState((s) => ({ ...(s || {}), link: { ...(s?.link || {}), ...(r.link || {}) } }));
  }, 'That change did not go through');

  const copy = async () => {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh);
      setCopied(true);
    } catch {
      // No clipboard (an old browser, an http page): select it for a manual copy.
      if (addressRef.current) { addressRef.current.focus(); addressRef.current.select(); }
    }
  };

  const link = state?.link || null;
  const close = () => { setOpen(false); setFresh(null); };

  return (
    <>
      <button type="button" className="btn btn-secondary btn-small tcal-subscribe-btn" data-cal-subscribe=""
              aria-haspopup="dialog" onClick={() => setOpen(true)} title="A private address Outlook, Apple or Google Calendar can subscribe to">
        <Icon name="link" size={13} />
        <span>{narrow ? 'Subscribe' : 'Subscribe in your calendar'}</span>
      </button>
      {open && (
        <Dialog label="Subscribe in your calendar" onClose={close} className="tcal-sub">
          <div className="tcal-sub-body" data-cal-subscribe-dialog="">
            <p className="tcal-sub-lead">
              A private address for Outlook, Apple Calendar or Google Calendar to follow. Your calendar fetches it on its
              own schedule (usually every few hours) and shows what is in it beside everything else you have. Changes
              flow one way, from Jarvis to your calendar.
            </p>
            {problem && <p className="tcal-sub-problem" role="alert">{problem}</p>}
            {state === null && !problem && <p className="tds-quiet">Checking…</p>}
            {state && !state.enabled && (
              <p className="tcal-sub-off" data-cal-subscribe-off="">
                Private calendar links are switched off by your administrator.
              </p>
            )}
            {state && state.enabled && !link && (
              <div className="tcal-sub-make">
                <p className="tds-quiet">You have no calendar link yet.</p>
                <button type="button" className="btn btn-primary btn-small" disabled={busy} data-cal-subscribe-make=""
                        onClick={make}>Make my link</button>
              </div>
            )}
            {state && state.enabled && link && (
              <>
                {fresh ? (
                  <div className="tcal-sub-address" data-cal-subscribe-address="">
                    <label className="tcal-sub-label" htmlFor="tcal-sub-url">Your address - copy it now</label>
                    <div className="tcal-sub-row">
                      <input id="tcal-sub-url" ref={addressRef} className="tcal-sub-url" type="text" readOnly value={fresh}
                             onFocus={(e) => e.target.select()} />
                      <button type="button" className="btn btn-primary btn-small tcal-sub-copy" onClick={copy} data-cal-subscribe-copy="">
                        <Icon name="copy" size={13} />{copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="tcal-sub-once">
                      Shown once: Jarvis keeps only a fingerprint of it, so it cannot be shown again. If you lose it,
                      Regenerate makes a new one.
                    </p>
                  </div>
                ) : (
                  <p className="tcal-sub-have" data-cal-subscribe-have="">
                    Your link was made {lower(relative(link.created_at))}
                    {link.last_used_at
                      ? <> and last fetched by a calendar {lower(relative(link.last_used_at))} ({fmtDateTime(link.last_used_at)}).</>
                      : <>. No calendar has fetched it yet.</>}
                    {' '}The address itself is not kept, so it cannot be shown again - Regenerate if you need it afresh.
                  </p>
                )}
                <fieldset className="tcal-sub-switches">
                  <legend className="tcal-sub-label">What it carries</legend>
                  {SWITCHES.map(([key, label, hint]) => (
                    <label key={key} className="tcal-sub-switch" data-cal-subscribe-switch={key}>
                      <input type="checkbox" checked={Boolean(link[key])} disabled={busy} onChange={(e) => toggle(key, e.target.checked)} />
                      <span><strong>{label}</strong><small>{hint}</small></span>
                    </label>
                  ))}
                </fieldset>
                <dl className="tcal-sub-how">
                  {INSTRUCTIONS.map(([app, how]) => (
                    <div key={app} className="tcal-sub-how-row"><dt>{app}</dt><dd>{how}</dd></div>
                  ))}
                </dl>
                <div className="tds-dialog-foot tcal-sub-foot">
                  <button type="button" className="tds-link is-danger" disabled={busy} onClick={remove} data-cal-subscribe-remove="">Remove link</button>
                  <button type="button" className="btn btn-secondary btn-small" disabled={busy} onClick={regenerate} data-cal-subscribe-regenerate="">
                    <Icon name="restore" size={13} />Regenerate
                  </button>
                  <button type="button" className="btn btn-primary btn-small" onClick={close}>Done</button>
                </div>
              </>
            )}
          </div>
          {confirmDialog}
        </Dialog>
      )}
    </>
  );
}
